import { useState, useCallback, memo, useEffect, useRef } from 'react'
import {
    // Tooltip,
    // Box,
    // Typography,
    IconButton,
    // LinearProgress,
    Grid,
    Typography,
    CircularProgress,
    // CircularProgress,
} from '@mui/material'
import { GoogleMap, OverlayView } from '@react-google-maps/api'
import { GoogleMapsWrapper } from '../hooks/GoogleMapsApiWrapper'
import { RefreshCw } from 'lucide-react'
import { useGlobalState } from '../../../context/GlobalStateContext'

interface FleetGpsObject {
    lat: number
    lng: number
    name: string
    status?: 'online' | 'offline' | 'idle'
    manufacturer?: string
    model?: string
    alias?: string
}

interface UniversalGoogleMapProps {
    gps?: { lat: string; lng: string }
    style: {
        width?: string | number
        height?: string | number
        backgroundColor?: string
        margin?: string | number
    }
    fleetGps?: FleetGpsObject[]
    mapZoom?: number
    onAssetClick?: (asset: FleetGpsObject) => void
}

interface CustomMarkerProps {
    position: { lat: number; lng: number }
    asset: FleetGpsObject
    onClick: (asset: FleetGpsObject) => void
}

const CustomMarker = ({ position, asset, onClick }: CustomMarkerProps) => {
    const [isHovered, setIsHovered] = useState(false)

    const getStatusColor = useCallback((status?: string) => {
        switch (status?.toUpperCase()) {
            case 'ONLINE':
            case 'RUNNING':
                return '#4caf50'
            case 'SHUTDOWN':
                return '#ddd5d5ff'
            case 'AWAKE':
                return '#ff9800'
            case 'OFFLINE':
            case 'IDLE':
                return '#9e9e9e'
            default:
                return '#9e9e9e'
        }
    }, [])

    return (
        <OverlayView
            position={position}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
            <div style={{ position: 'relative' }}>
                {isHovered && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: '25px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            color: 'white',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            whiteSpace: 'nowrap',
                            fontSize: '12px',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        }}
                    >
                        <div
                            style={{ fontWeight: 'bold', marginBottom: '4px' }}
                        >
                            {asset.alias || asset.name}
                        </div>
                        {asset.manufacturer && asset.model && (
                            <div style={{ marginBottom: '2px' }}>
                                {asset.manufacturer} {asset.model}
                            </div>
                        )}
                        <div style={{ textTransform: 'capitalize' }}>
                            Status: {asset.status || 'unknown'}
                        </div>

                        <div
                            style={{
                                position: 'absolute',
                                top: '100%',
                                left: '50%',
                                marginLeft: '-5px',
                                width: 0,
                                height: 0,
                                borderLeft: '5px solid transparent',
                                borderRight: '5px solid transparent',
                                borderTop: '5px solid rgba(0, 0, 0, 0.8)',
                            }}
                        />
                    </div>
                )}

                <div
                    onClick={() => onClick(asset)}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(asset.status),
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        cursor: 'pointer',
                        transform: isHovered ? 'scale(1.5)' : 'scale(1)',
                        transition: 'transform 0.2s ease',
                        position: 'relative',
                        zIndex: isHovered ? 1001 : 1,
                    }}
                >
                    {asset.status === 'online' && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                animation: 'pulse 2s infinite',
                            }}
                        />
                    )}
                </div>
            </div>
        </OverlayView>
    )
}

const UniversalGoogleMapsV2 = ({
    gps,
    style,
    fleetGps,
    mapZoom = 9,
    onAssetClick,
}: UniversalGoogleMapProps) => {
    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const { activeManufacturer } = oupUser
    const defaultCenter = useRef({
        lat: 28.446881475873266,
        lng: -81.1580598190508,
    }).current

    const center = gps
        ? { lat: parseFloat(gps.lat), lng: parseFloat(gps.lng) }
        : defaultCenter

    const containerStyle = {
        width: style.width ?? '100%',
        height: style.height ?? '400px',
        backgroundColor: style.backgroundColor,
        margin: style.margin,
    }

    const [googleMap, setGoogleMap] = useState<google.maps.Map | null>(null)
    const [displayedFleetGps, setDisplayedFleetGps] = useState<
        FleetGpsObject[]
    >(fleetGps || [])
    const [, setNeedsFit] = useState(true)
    const [selectedAsset, setSelectedAsset] = useState<FleetGpsObject | null>(
        null
    )

    const handleRefresh = useCallback(() => {
        setDisplayedFleetGps(fleetGps || [])
        setNeedsFit(true)
        setSelectedAsset(null)
    }, [fleetGps])

    useEffect(() => {
        handleRefresh()
    }, [activeManufacturer, fleetGps])

    const handleAssetClick = (asset: FleetGpsObject) => {
        setSelectedAsset(asset)
        if (onAssetClick) {
            onAssetClick(asset)
        }
    }

    useEffect(() => {
        if (!googleMap || !selectedAsset) return

        googleMap.setZoom(18)
        googleMap.setCenter({ lat: selectedAsset.lat, lng: selectedAsset.lng })
    }, [selectedAsset, googleMap])

    const onMapLoad = useCallback(
        (map) => {
            setGoogleMap(map)
            const bounds = new google.maps.LatLngBounds(center)

            if (displayedFleetGps && displayedFleetGps.length > 0) {
                displayedFleetGps.forEach(({ lat, lng }) => {
                    bounds.extend(new google.maps.LatLng(lat, lng))
                })
                map.fitBounds(bounds)
            } else if (gps) {
                bounds.extend(new google.maps.LatLng(center.lat, center.lng))
                map.fitBounds(bounds)
            } else {
                map.setCenter(center)
            }

            const listener: any = googleMap?.addListener('idle', () => {
                const assetCount =
                    (gps ? 1 : 0) + (displayedFleetGps?.length || 0)
                if (assetCount <= 1 && map.getZoom() > mapZoom) {
                    map.setZoom(mapZoom)
                }
                google.maps.event.removeListener(listener)
            })

            setNeedsFit(false)
        },
        [center, displayedFleetGps, gps, fleetGps, mapZoom]
    )

    const onUnmount = useCallback(() => {
        setGoogleMap(null)
    }, [])

    useEffect(() => {
        return () => setGoogleMap(null)
    }, [])

    return (
        <>
            <style>
                {`
                    @keyframes pulse {
                        0% { opacity: 1; }
                        50% { opacity: 0.3; }
                        100% { opacity: 1; }
                    }
                `}
            </style>
            <Grid
                container
                sx={{
                    backgroundColor: style.backgroundColor,
                    margin: style.margin,
                    position: 'relative',
                }}
            >
                <GoogleMapsWrapper>
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={center}
                        zoom={mapZoom}
                        onLoad={onMapLoad}
                        onUnmount={onUnmount}
                        options={{
                            zoomControl: true,
                            streetViewControl: false,
                            mapTypeControl: false,
                            fullscreenControl: true,
                        }}
                    >
                        {gps && gps.lng && gps.lat && (
                            <CustomMarker
                                position={{ lat: center.lat, lng: center.lng }}
                                asset={{
                                    lat: center.lat,
                                    lng: center.lng,
                                    name: 'Current Location',
                                    status: 'online',
                                }}
                                onClick={handleAssetClick}
                            />
                        )}

                        {displayedFleetGps?.map((asset) => (
                            <CustomMarker
                                key={`${asset.name}-${asset.lat}-${asset.lng}`}
                                position={{ lat: asset.lat, lng: asset.lng }}
                                asset={asset}
                                onClick={handleAssetClick}
                            />
                        ))}
                    </GoogleMap>
                </GoogleMapsWrapper>
            </Grid>
        </>
    )
}

export default memo(UniversalGoogleMapsV2)
